import { useCallback, useEffect, useRef, useState } from "react";
import useSWR, { useSWRConfig } from "swr";
import type { Cache, ScopedMutator } from "swr";
import type { TypedUrl } from "./common";
import { defaultFetcher } from "./client";

/**
 * Reads a single, definite model. Suspense is enabled per-hook (not via global
 * SWRConfig, so raw `useSWR` callers stay unaffected): while the request is in
 * flight the component suspends, and a 404/error throws to the nearest error
 * boundary; otherwise the resolved value is returned (never `undefined`).
 *
 * For a *conditional* fetch (the key may be absent), use `useMultiModel` with a
 * single nullable slot: `const [x] = useMultiModel([cond ? MODELS.X(id) : null])`.
 */
export function useModel<T = any>(url: TypedUrl<T>): T {
  const { data } = useSWR<T>(url, { suspense: true });
  return data as T;
}

type ModelInput = TypedUrl<unknown> | string | false | 0 | null | undefined;

/** Falsy slots (e.g. from `cond && MODEL(id)`) resolve to `undefined`. */
type FalsyInput = false | 0 | "" | null | undefined;

/** Response payload carried by a `TypedUrl` (`unknown` for a plain string). */
type ResponseOf<U> = [U] extends [TypedUrl<infer T>] ? T : unknown;

/**
 * The resolved value for one input slot: the bare response when the input is a
 * definite url, or `T | undefined` when the input may be falsy.
 */
type ModelResult<U> = [U] extends [Exclude<U, FalsyInput>]
  ? ResponseOf<U>
  : ResponseOf<Exclude<U, FalsyInput>> | undefined;

/** Options for `useMultiModel`. */
type MultiModelOpts = {
  /**
   * DEV ONLY (ignored in production builds): treat a failed request as an
   * EXPECTED absence instead of throwing to the nearest error boundary — which,
   * in dev, makes React log the whole captured stack trace. Each slot resolves to
   * `undefined` and the failure is reported as a single deduped `console.warn`.
   * Use for best-effort reads such as optional S3 images that legitimately 404,
   * and keep a Suspense + error boundary around the caller for the production
   * path, where this opt has no effect (the read still suspends and throws).
   * Disables suspense for these keys in dev, so the caller is responsible for
   * rendering a loading/empty placeholder.
   */
  quiet?: boolean;
  /**
   * Non-suspending read (prod AND dev). Each slot resolves to `undefined` while
   * the request is in flight and re-renders with the value once it lands, so the
   * caller renders immediately and treats the data as a progressive enhancement.
   * A failed request degrades to `undefined` (a single deduped `console.warn` in
   * dev) rather than throwing. Use for non-critical, after-the-fact data such as
   * tooltip descriptions. Unlike `quiet`, this is active in production too.
   */
  lazy?: boolean;
};

// Dev-only, deduped warning for `quiet` reads whose request failed. Logged once
// per key (a settled error stays cached, so without dedup it would fire on every
// re-render) so an expected-missing resource degrades to a single line instead of
// a full React error-boundary stack trace.
const quietWarned = new Set<string>();
function warnQuietFailure(key: string, error: unknown) {
  if (!import.meta.env.DEV || quietWarned.has(key)) return;
  quietWarned.add(key);
  console.warn(
    `useMultiModel: request for ${key} failed (quiet) —`,
    error instanceof Error ? error.message : error,
  );
}

// In-flight requests we fired ourselves, keyed by SWR cache key. The promise
// resolves (never rejects) when the request settles, so it's safe to throw to a
// Suspense boundary. MODULE-level on purpose: a component that suspends on its
// first render never commits, so component refs/state reset on every Suspense
// retry — only module state survives the retries to dedupe the in-flight request.
const inflight = new Map<string, Promise<void>>();
// Errors captured from our own fired requests. `mutate` does NOT write the error
// into SWR's cache (it only writes data on success), so we track it here to
// re-throw to the error boundary. Cleared on a later successful fetch of the key.
const fetchErrors = new Map<string, unknown>();

// Fire a request for `key` if it isn't already satisfied or in flight, writing the
// result straight into SWR's cache via `mutate` (which, unlike `preload`, persists
// the value independently of whether the React render commits — the property that
// makes this work under Suspense + router transitions). Concurrency comes for free:
// every slot's `ensureRequest` runs synchronously during render, so all requests
// start before the first suspend.
function ensureRequest(key: string, mutate: ScopedMutator, cache: Cache) {
  const entry = cache.get(key);
  if (entry && entry.data !== undefined) return; // already have data
  if (fetchErrors.has(key)) return; // settled error → the read below throws it
  if (inflight.has(key)) return; // already firing
  const req = mutate(key, defaultFetcher(key), { revalidate: false }).then(
    () => {
      fetchErrors.delete(key);
    },
    (err: unknown) => {
      fetchErrors.set(key, err);
    },
  );
  inflight.set(
    key,
    req.finally(() => inflight.delete(key)),
  );
}

/**
 * Reads several models needed together, concurrently and cache-coherent.
 *
 * - Fires ALL requests up front (via `mutate`) so there's no request waterfall.
 * - Reads each on its own SWR cache key → shares cache with `useModel` and responds to
 *   `mutate(key)` invalidation (unlike a single combined `Promise.all` key would).
 *
 * Why `mutate` and not `preload`: SWR's Suspense path special-cases a `preload`ed key
 * by `use()`-ing a fresh `boundMutate(req)` promise on every render. Under a React-Router
 * navigation (a transition) that unstable promise identity never settles → infinite
 * Suspense loop. `mutate` writes the fetched value into the cache directly (even when the
 * render that fired it gets discarded), and we drive Suspense ourselves by throwing a
 * STABLE promise from {@link inflight}, sidestepping that branch entirely.
 *
 * Returns a tuple positionally matching the input: a slot is `T | undefined` only when
 * its input may be falsy (`null`/`undefined`, or the `false`/`""` produced by
 * `cond && MODEL(id)`) — this is how conditional and absent fetches are expressed.
 * A definite url yields a bare `T`.
 *
 * Contract: pass a STABLE-LENGTH array. It is all-or-nothing: suspends until every
 * present entry resolves. For partial rendering, wrap an independent section in its
 * own `<Suspense>` instead.
 *
 * Pass `{ quiet: true }` to degrade failures to `undefined` + a single dev warning
 * instead of throwing — intended for optional slots (see {@link MultiModelOpts}).
 */
export function useMultiModel<const Urls extends ReadonlyArray<ModelInput>>(
  urls: Urls,
  opts?: MultiModelOpts,
): { -readonly [K in keyof Urls]: ModelResult<Urls[K]> } {
  // Honoured in dev only: production keeps the suspend-and-throw behaviour so
  // failures still surface to an error boundary rather than being swallowed.
  const quiet = (opts?.quiet ?? false) && import.meta.env.DEV;
  // `lazy` is honoured in production too — a genuinely non-suspending read.
  const lazy = opts?.lazy ?? false;
  // Both modes skip our manual suspend/throw plumbing and let plain `useSWR`
  // drive the fetch, degrading failures to `undefined` instead of throwing.
  const nonSuspending = quiet || lazy;
  const { cache, mutate } = useSWRConfig();

  // Kick off every request before any read can suspend → fully parallel.
  if (!nonSuspending) {
    for (const u of urls) if (u) ensureRequest(String(u), mutate, cache);
  }

  // Read each on its own cache key. `suspense: false` because we manage suspension
  // ourselves below; `revalidateOnMount: false` (suspending mode) avoids a redundant
  // re-fetch on the render that commits — `ensureRequest`/`mutate(key)` already keep
  // the cache fresh. Non-suspending keys fire their own request (no `ensureRequest`),
  // so they keep the default mount behaviour.
  const reads = urls.map((u) =>
    // eslint-disable-next-line react-hooks/rules-of-hooks -- stable-length array contract
    useSWR(u || undefined, {
      suspense: false,
      revalidateOnMount: nonSuspending ? undefined : false,
    }),
  );

  if (!nonSuspending) {
    const pending: Promise<void>[] = [];
    let errorToThrow: unknown;
    urls.forEach((u, i) => {
      if (!u) return;
      const key = String(u);
      if (fetchErrors.has(key)) {
        if (errorToThrow === undefined) errorToThrow = fetchErrors.get(key);
        return;
      }
      // Suspend only on a genuinely in-flight request. A key with no data, no error
      // and no in-flight promise resolved to `undefined` (e.g. a 304) — return it as
      // `undefined` rather than re-firing, which would loop forever.
      if (reads[i].data === undefined) {
        const p = inflight.get(key);
        if (p) pending.push(p);
      }
    });
    // Re-throw the captured fetch error to the nearest error boundary, and throw a
    // promise to the nearest Suspense boundary — both are deliberate (the error is
    // surfaced as-is; the promise is React's documented suspend signal).
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    if (errorToThrow !== undefined) throw errorToThrow;
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    if (pending.length) throw Promise.all(pending);
  }

  return urls.map((u, i) => {
    if (!u) return undefined;
    if (nonSuspending && reads[i].error) {
      warnQuietFailure(String(u), reads[i].error);
      return undefined;
    }
    return reads[i].data;
  }) as { -readonly [K in keyof Urls]: ModelResult<Urls[K]> };
}

export function useMutation<TIn, TOut>(
  handler: (input: TIn) => Promise<TOut> | TOut,
): [(input: TIn) => Promise<TOut> | undefined, { isLoading: boolean }] {
  if (!handler) throw new Error("handler can't be empty");
  const [state, setState] = useState<"initial" | "loading">("initial");
  const lastHandlerRef = useRef(handler);
  useEffect(() => {
    lastHandlerRef.current = handler;
  }, [handler]);
  const lastStateRef = useRef(state);
  useEffect(() => {
    lastStateRef.current = state;
  }, [state]);
  const mutationHandler = useCallback(
    (data: TIn): Promise<TOut> | undefined => {
      if (lastStateRef.current !== "initial") return;
      setState("loading");
      const result = lastHandlerRef.current(data);
      const promiseLike =
        result && typeof (result as Promise<TOut>).then === "function"
          ? (result as Promise<TOut>)
          : Promise.resolve(result as TOut);
      return promiseLike.then(
        (resolved) => {
          setState("initial");
          return resolved;
        },
        (e) => {
          setState("initial");
          return Promise.reject(e);
        },
      );
    },
    [],
  );
  return [mutationHandler, { isLoading: state === "loading" }];
}
